import { BAND_YELLOW } from '../audit/grades';
import { PARTY_LABEL } from '../audit/requirements';
import { daysOverdue } from '../audit/windows';
import type { StoreRow } from './dashboard';
import { plural } from '../ui/plural';

/**
 * "Call this week" (B7, owner rulings 2026-10-25): the stores that most
 * need a call, each with a one-line reason. Ranked LATENESS FIRST (his
 * ruling): total days overdue across every audit clock, then overdue tasks
 * nobody has touched, then a failing Quality grade (below 60). A store with
 * none of the three isn't listed. Same data as the board and the grade
 * drivers; pure.
 */
export const CALL_LIST_SIZE = 10;

export interface CallItem {
  row: StoreRow;
  /** Days overdue summed over the store's overdue audit clocks. */
  daysLate: number;
  untouched: number;
  /** The Quality grade when it's failing (shown rounded, as on the chip), else null. */
  failingQuality: number | null;
  /** "DM Visit 54d + DM Facility Audit 40d overdue · 2 untouched tasks · Quality 52" */
  reason: string;
}

const clockLabel = (name: string, party: string) =>
  party === 'auditor' || party === 'store' ? `${name} (${PARTY_LABEL[party]})` : name;

export function callReasons(rows: readonly StoreRow[], now: Date): CallItem[] {
  const items: CallItem[] = [];
  for (const row of rows) {
    const late = row.clocks
      .filter((c) => c.status === 'overdue')
      .map((c) => ({ label: clockLabel(c.template.name, c.party), days: daysOverdue(c.nextDue, now) }))
      .sort((a, b) => b.days - a.days);
    const daysLate = late.reduce((n, c) => n + c.days, 0);
    const untouched = row.untouchedOverdueTasks;
    const quality = row.grade.quality;
    const failingQuality = quality !== null && Math.round(quality) < BAND_YELLOW ? Math.round(quality) : null;
    if (daysLate <= 0 && untouched <= 0 && failingQuality === null) continue;
    const parts: string[] = [];
    if (late.length > 0) parts.push(`${late.map((c) => `${c.label} ${plural(c.days, 'day')}`).join(' + ')} overdue`);
    if (untouched > 0) parts.push(`${untouched} untouched overdue ${untouched === 1 ? 'task' : 'tasks'}`);
    if (failingQuality !== null) parts.push(`Quality ${failingQuality}`);
    items.push({ row, daysLate, untouched, failingQuality, reason: parts.join(' · ') });
  }
  return items;
}

/** Lateness first, then untouched tasks, then the lowest failing Quality, then store number. */
export function buildCallList(rows: readonly StoreRow[], now: Date, size = CALL_LIST_SIZE): CallItem[] {
  return callReasons(rows, now)
    .sort(
      (a, b) =>
        b.daysLate - a.daysLate ||
        b.untouched - a.untouched ||
        (a.failingQuality ?? Infinity) - (b.failingQuality ?? Infinity) ||
        a.row.store.storeNumber.localeCompare(b.row.store.storeNumber, undefined, { numeric: true }),
    )
    .slice(0, size);
}
